import DOMPurify, { type Config } from "dompurify";
import { forwardRef, useMemo } from "react";

/**
 * The only place status-alert HTML may reach the DOM (spec 02 §3.4).
 *
 * The server already sanitised the fragment. This pass is defence in depth:
 * the same tag allowlist, with the portal variant also dropping style.
 * The hook is registered once, on this instance only, so it cannot leak
 * onto the Knowledge Base sanitiser.
 */

const ALLOWED_TAGS = ["p", "br", "strong", "ul", "ol", "li", "a"];
const ALLOWED_ATTR = ["href", "start", "style", "target", "rel"];
const ALLOWED_URI_REGEXP = /^(?:https?:|mailto:)/i;

const EMAIL_CONFIG: Config = {
  ALLOWED_TAGS,
  ALLOWED_ATTR,
  ALLOWED_URI_REGEXP,
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: false,
};

const PORTAL_CONFIG: Config = {
  ...EMAIL_CONFIG,
  FORBID_ATTR: ["style"],
};

const purify = DOMPurify(window);

// `start` is not a URL. DOMPurify otherwise checks it against ALLOWED_URI_REGEXP
// and drops the list number. Keep a plain integer on <ol> only.
const ORDERED_LIST_START = /^-?\d{1,15}$/;

purify.addHook("uponSanitizeAttribute", (node, data) => {
  if (data.attrName !== "start") return;
  const tag = node instanceof Element ? node.tagName.toUpperCase() : "";
  if (tag === "OL" && ORDERED_LIST_START.test(data.attrValue)) {
    data.forceKeepAttr = true;
    return;
  }
  data.keepAttr = false;
});

purify.addHook("afterSanitizeAttributes", (node) => {
  if (!(node instanceof Element) || node.tagName.toUpperCase() !== "A") return;
  if (!node.getAttribute("href")) return;
  node.setAttribute("target", "_blank");
  node.setAttribute("rel", "noopener noreferrer");
});

export const RichSummary = forwardRef<
  HTMLDivElement,
  {
    html: string;
    variant: "email" | "portal";
    className?: string;
    id?: string;
  }
>(function RichSummary({ html, variant, className, id }, ref) {
  const clean = useMemo(
    () => purify.sanitize(html, variant === "email" ? EMAIL_CONFIG : PORTAL_CONFIG),
    [html, variant],
  );
  return (
    <div
      ref={ref}
      id={id}
      className={className}
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
});
